import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';

const slides = [
  {
    id: 1, type: 'cover',
    title: "Développement du langage (0-3 ans)",
    subtitle: "Repères - Facteurs - Troubles - Posture professionnelle",
    badge: "APDR de la VAE"
  },
  {
    id: 2, type: 'objectives',
    title: "Objectifs du cours",
    items: [
      "Comprendre les grandes étapes du langage entre 0 et 3 ans",
      "Identifier ce qui favorise (ou freine) le développement langagier",
      "Repérer des signes d'alerte sans poser de diagnostic",
      "Savoir quoi proposer au quotidien (jeux, lecture, routines)",
      "Adopter une posture professionnelle soutenante"
    ]
  },
  {
    id: 3, type: 'content',
    title: "1. Pourquoi le langage est fondamental ?",
    intro: "Le langage permet à l'enfant de...",
    items: [
      "Entrer en relation et communiquer ses besoins",
      "Exprimer ses émotions et se réguler",
      "Structurer sa pensée et comprendre le monde",
      "Développer la socialisation et l'autonomie",
      "Accéder progressivement aux apprentissages"
    ],
    color: "#7C3AED"
  },
  {
    id: 4, type: 'stage',
    title: "2. Étapes du langage — 0 à 6 mois",
    label: "Communication préverbale",
    left: {
      title: "Ce qui se passe",
      items: ["Pleurs différenciés", "Sourire social", "Regard et contact visuel", "Gazouillis", "Réaction à la voix"]
    },
    right: {
      title: "Rôle du professionnel",
      items: ["Répondre aux vocalisations", "Mettre des mots sur ce que vit l'enfant", "Favoriser le face-à-face et l'attention conjointe", "Instaurer des routines verbalisées"]
    },
    color: "#E75480"
  },
  {
    id: 5, type: 'stage',
    title: "2. Étapes du langage — 6 à 12 mois",
    label: "Babillage & compréhension",
    left: {
      title: "Ce qui se passe",
      items: ["Babillage (ba-ba, ma-ma...)", "Réaction au prénom", "Compréhension de mots simples", "Pointage / gestes", "Imitation sonore"]
    },
    right: {
      title: "Idées d'activités",
      items: ["Jeux d'imitation (coucou, au revoir)", "Comptines répétitives", "Imagiers et livres cartonnés", "Nommer les objets du quotidien"]
    },
    color: "#3B82F6"
  },
  {
    id: 6, type: 'content',
    title: "2. Étapes du langage — 12 à 24 mois",
    intro: "Explosion lexicale",
    items: [
      "Premiers mots puis augmentation rapide du vocabulaire",
      "Compréhension > expression (l'enfant comprend plus qu'il ne dit)",
      "Association de 2 mots (ex. \"encore eau\")",
      "Désignation d'images et d'objets"
    ],
    color: "#D97706"
  },
  {
    id: 7, type: 'content',
    title: "2. Étapes du langage — 2 à 3 ans",
    intro: "Structuration",
    items: [
      "Phrases simples puis plus longues",
      "Utilisation du \"je\"",
      "Questions (quoi, où, pourquoi...)",
      "Début de narration (raconter une action, un événement)",
      "Langage au service de l'autonomie"
    ],
    color: "#059669"
  },
  {
    id: 8, type: 'content',
    title: "3. Ce qui favorise le langage",
    intro: "Facteurs positifs",
    items: [
      "Interactions de qualité (regard, disponibilité, tour de rôle)",
      "Attention conjointe (regarder la même chose que l'enfant)",
      "Lecture quotidienne + échanges autour des images",
      "Comptines, jeux de doigts, répétition",
      "Jeu symbolique (dînette, poupée, voitures...)",
      "Environnement sécurisant et routines verbalisées"
    ],
    color: "#0891B2"
  },
  {
    id: 9, type: 'two-col',
    title: "4. Pratiques : avantages / limites",
    left: {
      title: "Lecture d'histoires",
      pros: "Vocabulaire, compréhension, lien affectif. Échanges autour des images.",
      cons: "Limite si lecture passive sans interaction. Adapter le livre à l'âge."
    },
    right: {
      title: "Comptines & jeux",
      pros: "Rythme, mémoire auditive, imitation. Participation active.",
      cons: "Limite si trop rapide / pas assez répétée. Favoriser la participation plutôt que la performance."
    },
    color: "#7C3AED"
  },
  {
    id: 10, type: 'alert',
    title: "4. Point de vigilance : les écrans avant 3 ans",
    intro: "Pourquoi c'est un frein ?",
    items: [
      "Réduction des interactions adultes-enfants (le facteur le plus important)",
      "Moins d'attention conjointe et de tour de rôle",
      "Risque de difficultés d'attention",
      "Les écrans ne remplacent pas l'échange humain"
    ],
    color: "#DC2626"
  },
  {
    id: 11, type: 'alert-signs',
    title: "5. Signes d'alerte (repères)",
    items: [
      { age: "Avant 1 an", sign: "Pas de babillage / peu de réactions à la voix" },
      { age: "Vers 18 mois", sign: "Aucun mot, pas de pointage" },
      { age: "Vers 2 ans", sign: "Vocabulaire très limité, pas d'association de mots" },
      { age: "Vers 3 ans", sign: "Discours difficilement compréhensible, peu de phrases" }
    ]
  },
  {
    id: 12, type: 'troubles',
    title: "6. Difficultés & troubles (sans diagnostic)",
    items: [
      { name: "Retard de langage", desc: "Progression possible avec stimulation et suivi" },
      { name: "Trouble auditif", desc: "Impact sur la perception des sons et des mots" },
      { name: "TDL", desc: "Trouble développemental du langage : difficulté durable de compréhension / expression" },
      { name: "TSA", desc: "Troubles de la communication sociale : interaction et usage du langage" }
    ]
  },
  {
    id: 13, type: 'content',
    title: "7. Posture professionnelle",
    intro: "Bonnes pratiques",
    items: [
      "Se mettre à hauteur d'enfant, regarder, écouter",
      "Parler lentement, phrases simples, mots précis",
      "Reformuler sans \"corriger\" brutalement",
      "Laisser le temps de réponse (silence utile)",
      "Valoriser les tentatives de communication",
      "Observer, noter, échanger avec les parents, orienter si nécessaire"
    ],
    color: "#059669"
  },
  {
    id: 14, type: 'activities',
    title: "8. Activités concrètes (0-3 ans)",
    intro: "Idées simples du quotidien",
    items: [
      { icon: "📖", name: "Imagiers / photos réelles" },
      { icon: "🎭", name: "Marionnettes et jeux de \"coucou\"" },
      { icon: "🎵", name: "Boîte à sons / instruments doux" },
      { icon: "🍳", name: "Jeu symbolique commenté (dînette, poupée, garage)" },
      { icon: "🔄", name: "Routines verbalisées : change, repas, sieste" },
      { icon: "❓", name: "Lecture interactive : \"Où est... ?\", \"Qu'est-ce que tu vois ?\"" }
    ]
  },
  {
    id: 15, type: 'conclusion',
    title: "Conclusion",
    message: "Le langage ne s'enseigne pas : il se vit dans l'interaction.",
    sub: "Entre 0 et 3 ans, la qualité relationnelle prime sur la quantité de mots."
  }
];

function CoverSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '420px', textAlign: 'center', padding: '2rem', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', borderRadius: '0.5rem', color: 'white' }}>
      <div style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem 1.5rem', borderRadius: '2rem', fontSize: '0.8rem', fontWeight: '600', marginBottom: '2rem', letterSpacing: '0.05em' }}>
        {slide.badge}
      </div>
      <h1 style={{ fontSize: '2.2rem', fontWeight: 'bold', marginBottom: '1rem', lineHeight: 1.2 }}>{slide.title}</h1>
      <p style={{ fontSize: '1.1rem', opacity: 0.8, maxWidth: '500px' }}>{slide.subtitle}</p>
      <div style={{ marginTop: '2rem', width: '60px', height: '3px', background: 'linear-gradient(90deg, #E75480, #7C3AED)', borderRadius: '2px' }} />
    </div>
  );
}

function ObjectivesSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '1.5rem' }}>{slide.title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', background: '#F9FAFB', borderRadius: '0.75rem', padding: '1rem', borderLeft: '3px solid #7C3AED' }}>
            <span style={{ background: '#7C3AED', color: 'white', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.8rem', flexShrink: 0 }}>{i + 1}</span>
            <span style={{ color: '#374151', lineHeight: 1.5 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.5rem' }}>{slide.title}</h2>
      {slide.intro && <p style={{ color: slide.color, fontWeight: '600', fontSize: '0.9rem', marginBottom: '1.5rem' }}>{slide.intro}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 1rem', background: `${slide.color}08`, borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: slide.color, marginTop: '6px', flexShrink: 0 }} />
            <span style={{ color: '#374151', lineHeight: 1.6 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.25rem' }}>{slide.title}</h2>
      <span style={{ display: 'inline-block', background: `${slide.color}15`, color: slide.color, padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.8rem', fontWeight: '600', marginBottom: '1.5rem' }}>{slide.label}</span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {[slide.left, slide.right].map((col, ci) => (
          <div key={ci} style={{ background: ci === 0 ? `${slide.color}08` : '#F0FDF4', borderRadius: '0.75rem', padding: '1.25rem', border: `1px solid ${ci === 0 ? slide.color + '20' : '#BBF7D020'}` }}>
            <h3 style={{ fontWeight: '700', fontSize: '0.85rem', color: ci === 0 ? slide.color : '#059669', marginBottom: '0.75rem' }}>{col.title}</h3>
            {col.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ color: ci === 0 ? slide.color : '#059669', fontSize: '0.8rem', marginTop: '2px' }}>{ci === 0 ? '●' : '▸'}</span>
                <span style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TwoColSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '1.5rem' }}>{slide.title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {[slide.left, slide.right].map((col, ci) => (
          <div key={ci} style={{ background: '#F9FAFB', borderRadius: '0.75rem', padding: '1.25rem', border: `1px solid ${slide.color}15` }}>
            <h3 style={{ fontWeight: '700', color: slide.color, marginBottom: '0.75rem' }}>{col.title}</h3>
            <div style={{ background: '#ECFDF5', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#059669' }}>Avantages</span>
              <p style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.25rem' }}>{col.pros}</p>
            </div>
            <div style={{ background: '#FEF2F2', borderRadius: '0.5rem', padding: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#DC2626' }}>Limites</span>
              <p style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.25rem' }}>{col.cons}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#DC2626', marginBottom: '0.5rem' }}>{slide.title}</h2>
      <p style={{ color: '#991B1B', fontWeight: '600', fontSize: '0.9rem', marginBottom: '1.5rem' }}>{slide.intro}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', background: '#FEF2F2', borderRadius: '0.75rem', borderLeft: '3px solid #DC2626' }}>
            <span style={{ fontSize: '1.2rem' }}>&#9888;</span>
            <span style={{ color: '#374151', lineHeight: 1.6 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertSignsSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '1.5rem' }}>{slide.title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#FFFBEB', borderRadius: '0.75rem', border: '1px solid #F59E0B30' }}>
            <div style={{ background: '#F59E0B', color: 'white', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontWeight: '700', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.age}</div>
            <span style={{ color: '#374151', lineHeight: 1.5 }}>{item.sign}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TroublesSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '1.5rem' }}>{slide.title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ padding: '1.25rem', background: '#F9FAFB', borderRadius: '0.75rem', borderTop: `3px solid ${['#7C3AED', '#3B82F6', '#D97706', '#0891B2'][i]}` }}>
            <h3 style={{ fontWeight: '700', color: ['#7C3AED', '#3B82F6', '#D97706', '#0891B2'][i], marginBottom: '0.5rem', fontSize: '0.95rem' }}>{item.name}</h3>
            <p style={{ color: '#6B7280', fontSize: '0.9rem', lineHeight: 1.5 }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivitiesSlide({ slide }) {
  return (
    <div style={{ padding: '2rem', minHeight: '420px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.5rem' }}>{slide.title}</h2>
      <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>{slide.intro}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {slide.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#F0F9FF', borderRadius: '0.75rem', border: '1px solid #BAE6FD' }}>
            <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
            <span style={{ color: '#374151', fontSize: '0.9rem', lineHeight: 1.4 }}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConclusionSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '420px', textAlign: 'center', padding: '3rem', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', borderRadius: '0.5rem', color: 'white' }}>
      <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '2rem' }}>{slide.title}</h2>
      <blockquote style={{ fontSize: '1.3rem', fontStyle: 'italic', maxWidth: '600px', lineHeight: 1.6, opacity: 0.95 }}>
        "{slide.message}"
      </blockquote>
      <p style={{ marginTop: '1.5rem', opacity: 0.75, fontSize: '1rem' }}>{slide.sub}</p>
      <div style={{ marginTop: '2rem', width: '60px', height: '3px', background: 'linear-gradient(90deg, #E75480, #7C3AED)', borderRadius: '2px' }} />
    </div>
  );
}

const RENDERERS = {
  cover: CoverSlide, objectives: ObjectivesSlide, content: ContentSlide,
  stage: StageSlide, 'two-col': TwoColSlide, alert: AlertSlide,
  'alert-signs': AlertSignsSlide, troubles: TroublesSlide,
  activities: ActivitiesSlide, conclusion: ConclusionSlide
};

export default function FormationLangagePresentation() {
  const [current, setCurrent] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const synthRef = useRef(window.speechSynthesis);
  const slide = slides[current];

  const speak = (text) => {
    synthRef.current.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR'; u.rate = 0.9;
    u.onend = () => setSpeaking(false);
    setSpeaking(true);
    synthRef.current.speak(u);
  };
  const stopSpeak = () => { synthRef.current.cancel(); setSpeaking(false); };

  const readSlide = () => {
    if (speaking) { stopSpeak(); return; }
    let text = slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle;
    if (slide.intro) text += slide.intro + '. ';
    if (slide.items) text += (Array.isArray(slide.items) ? slide.items.map(i => typeof i === 'string' ? i : (i.name || i.age + ': ' + i.sign)).join('. ') : '');
    if (slide.message) text += slide.message + '. ' + (slide.sub || '');
    speak(text);
  };

  const prev = () => { stopSpeak(); setCurrent(c => Math.max(0, c - 1)); };
  const next = () => { stopSpeak(); setCurrent(c => Math.min(slides.length - 1, c + 1)); };
  const Renderer = RENDERERS[slide.type] || ContentSlide;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ background: 'white', borderRadius: '1rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', padding: '0.75rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Développement du langage (0-3 ans)</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={readSlide} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
              {speaking ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <span style={{ fontSize: '0.85rem' }}>{current + 1} / {slides.length}</span>
          </div>
        </div>

        <Renderer slide={slide} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB' }}>
          <button onClick={prev} disabled={current === 0} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #D1D5DB', background: 'white', cursor: current === 0 ? 'not-allowed' : 'pointer', opacity: current === 0 ? 0.4 : 1, fontSize: '0.9rem', color: '#374151' }}>
            <ChevronLeft size={18} /> Précédent
          </button>
          <div style={{ display: 'flex', gap: '4px' }}>
            {slides.map((_, i) => (
              <div key={i} onClick={() => { stopSpeak(); setCurrent(i); }} style={{ width: i === current ? '24px' : '8px', height: '8px', borderRadius: '4px', background: i === current ? '#0f3460' : '#D1D5DB', cursor: 'pointer', transition: 'all 0.2s' }} />
            ))}
          </div>
          <button onClick={next} disabled={current === slides.length - 1} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', background: current === slides.length - 1 ? '#D1D5DB' : '#0f3460', color: 'white', cursor: current === slides.length - 1 ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}>
            Suivant <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
